import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { DateTime, Duration } from 'luxon';

import styled from '@emotion/styled';

import { useMutation, useQuery } from '@apollo/client/react';

import { FontWeight, SpinLoader } from '@clutter/clean';

import {
  CallbackWindow,
  CallbackWindowsDocument,
  CustomerTicket__ContactPreference,
  ErrorCodeEnum,
  ZendeskCallbackRequestCreateDocument,
} from '@graphql/platform';

import { useStorageCheckoutContext } from '@root/components/checkout/context';
import { WT_VISITOR_TOKEN, useTrack } from '@root/initializers/wt';
import { PHONE_MASK } from '@root/resources/masks';
import { ServiceEnum } from '@root/resources/types/service';
import { phone as phoneValidator } from '@root/resources/validator';

import { useCreateLead } from '@shared/lead_form';
import { formatCallbackWindow } from '@shared/qualified_contact_widget/callback_windows';

import { GQLError } from '@utils/gql_errors';
import { titleize } from '@utils/text';

import callConfirmedCheck from '@images/checkout/challenger/call_confirmed_check.svg';
import callingYouHeadset from '@images/checkout/challenger/calling_you_headset.svg';
import closeIcon from '@images/checkout/challenger/close.svg';
import phoneIcon from '@images/checkout/challenger/phone.svg';

import { CHALLENGER as T } from './tokens';
import { createTextMaskInputElement } from 'text-mask-core';

/**
 * Talk with an expert — Figma workflow:
 * Desktop: 707:14647 empty · 707:14875 slot selected · 707:15105 call-now
 *   707:14250 Call confirmed · 707:14448 We’re calling you
 *   707:14917 opens as a top-right popover under “Talk with an expert”
 * Mobile: 707:23363 form · 707:23349 nav (phone + Close) while open
 */

const DESKTOP = '@media (min-width: 840px)';
const MOBILE = '@media (max-width: 839px)';

const POPOVER_WIDTH = 420;
const POPOVER_GAP = 8;

/**
 * Figma 707:14917 / 707:23363 — no dimmed scrim; click-outside closes.
 * Portaled to body so sticky nav doesn’t clip the card. Mobile `$offsetTop`
 * clears the measured nav + gap so title / “Call me now” stay fully visible.
 */
const Overlay = styled.div<{ $offsetTop?: number }>`
  position: fixed;
  z-index: 1000;
  background: transparent;

  ${DESKTOP} {
    inset: 0;
  }

  ${MOBILE} {
    top: ${({ $offsetTop }) =>
      $offsetTop != null ? `${$offsetTop}px` : '72px'};
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: ${T.space[16]};
    box-sizing: border-box;
    overflow-y: auto;
  }
`;

const Dialog = styled.div<{
  $top?: number;
  $right?: number;
  $offsetTop?: number;
}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: ${POPOVER_WIDTH}px;
  box-sizing: border-box;
  background: ${T.color.bgPrimary};
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.08),
    0 2px 2px rgba(0, 0, 0, 0.04);
  font-family: ${T.font.sans};
  color: ${T.color.textPrimary};
  overflow: hidden;

  /* Figma 707:14917 — anchored under nav Talk CTA (top ~76, width 420). */
  ${DESKTOP} {
    gap: ${T.space[16]};
    padding: ${T.space[24]} 0;
    border-radius: 16px;
    position: fixed;
    top: ${({ $top }) => ($top != null ? `${$top}px` : '76px')};
    right: ${({ $right }) => ($right != null ? `${$right}px` : '32px')};
    left: auto;
    max-height: calc(
      100dvh - ${({ $top }) => ($top != null ? $top : 76)}px - 16px
    );
    overflow-y: auto;
  }

  /* Figma 707:23363 — radius-md card under sticky nav with clear air gap. */
  ${MOBILE} {
    gap: ${T.space[12]};
    padding: ${T.space[16]} 0;
    border-radius: ${T.radius.md};
    position: relative;
    max-height: calc(
      100dvh - ${({ $offsetTop }) => ($offsetTop != null ? $offsetTop : 72)}px -
        32px
    );
    overflow-y: auto;
    flex-shrink: 0;
  }
`;

const SuccessDialog = styled(Dialog)`
  gap: ${T.space[16]};

  ${DESKTOP} {
    padding: ${T.space[24]};
  }

  ${MOBILE} {
    padding: ${T.space[16]};
  }
`;

const TopBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
  width: 100%;
  box-sizing: border-box;
  border-bottom: 1px solid ${T.color.borderDefault};

  ${DESKTOP} {
    padding: 0 ${T.space[24]};
  }

  ${MOBILE} {
    padding: 0 ${T.space[16]};
  }
`;

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${DESKTOP} {
    gap: ${T.space[8]};
  }

  ${MOBILE} {
    gap: ${T.space[4]};
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  gap: ${T.space[16]};
`;

const Title = styled.h2`
  margin: 0;
  flex: 1;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};

  ${DESKTOP} {
    font-size: 20px;
    line-height: 23px;
  }

  ${MOBILE} {
    font-size: 16px;
    line-height: 22px;
  }
`;

/** Desktop popover only — mobile closes via nav phone toggle / overlay. */
const CloseButton = styled.button`
  appearance: none;
  display: none;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;

  img {
    display: block;
    width: 14px;
    height: 14px;
  }

  ${DESKTOP} {
    display: inline-flex;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  width: 100%;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textTertiary};

  ${DESKTOP} {
    font-size: 14px;
    line-height: 21px;
  }

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }
`;

const CallNowCard = styled.button<{ $selected?: boolean }>`
  appearance: none;
  display: flex;
  align-items: center;
  width: 100%;
  padding: ${T.space[8]} ${T.space[16]};
  box-sizing: border-box;
  border: 1px solid ${T.color.borderBrand};
  border-radius: ${T.radius.md};
  background: ${({ $selected }) =>
    $selected ? T.color.bgSelected : T.color.bgPrimary};
  cursor: pointer;
  text-align: left;
  font-family: ${T.font.sans};
`;

const CallNowBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
`;

const AvailableRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${T.space[4]};
  height: 20px;
  width: 100%;
`;

const AvailableDot = styled.span`
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${T.color.iconBrand};
  flex-shrink: 0;
`;

const AvailableLabel = styled.span`
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textBrand};

  ${DESKTOP} {
    font-size: 12px;
    line-height: 16px;
  }

  ${MOBILE} {
    font-size: 10px;
    line-height: 14px;
  }
`;

const CallNowLabel = styled.span`
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};

  ${DESKTOP} {
    font-size: 16px;
    line-height: 24px;
  }

  ${MOBILE} {
    font-size: 14px;
    line-height: 20px;
  }
`;

/** Icon box 24×24; vector leaf 18×18. */
const PhoneIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;

  img {
    display: block;
    width: 18px;
    height: 18px;
  }
`;

const TimesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[12]};
  width: 100%;
  max-height: 273px;
  box-sizing: border-box;
  overflow: auto;

  ${DESKTOP} {
    padding-bottom: ${T.space[12]};
  }

  ${MOBILE} {
    padding-bottom: ${T.space[24]};
  }
`;

const OrPickLabel = styled.p`
  margin: 0;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textTertiary};

  ${DESKTOP} {
    font-size: 14px;
    line-height: 21px;
  }

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }
`;

const SlotGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${T.space[12]};
  width: 100%;
`;

const SlotButton = styled.button<{ $selected?: boolean }>`
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-height: 45px;
  padding: ${T.space[12]} ${T.space[16]};
  box-sizing: border-box;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? T.color.borderBrand : T.color.borderDefault};
  border-radius: ${T.radius.md};
  background: ${({ $selected }) =>
    $selected ? T.color.bgSelected : T.color.bgPrimary};
  cursor: pointer;
  font-family: ${T.font.sans};
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};
  text-align: left;

  ${DESKTOP} {
    font-size: 14px;
    line-height: 21px;
  }

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }
`;

const DayNote = styled.p`
  margin: 0;
  grid-column: 1 / -1;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textTertiary};
  text-align: center;

  ${DESKTOP} {
    font-size: 14px;
    line-height: 21px;
  }

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }
`;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  width: 100%;
`;

const BottomBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
  width: 100%;
  box-sizing: border-box;

  ${DESKTOP} {
    padding: 0 ${T.space[24]};
  }

  ${MOBILE} {
    padding: 0 ${T.space[16]};
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};

  ${DESKTOP} {
    font-size: 16px;
    line-height: 24px;
  }

  ${MOBILE} {
    font-size: 14px;
    line-height: 20px;
  }
`;

/**
 * Desktop 707:14683 — gap 8 between 74px input slots.
 * Mobile 707:23363 — gap 4 between fields.
 */
const Fields = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${DESKTOP} {
    gap: ${T.space[8]};
  }

  ${MOBILE} {
    gap: ${T.space[4]};
  }
`;

/** Figma 707:14684 / 707:23400 — Input frame 74px (54px control + helper slot). */
const FieldSlot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  min-height: 74px;
`;

const FloatField = styled.div`
  position: relative;
  width: 100%;
`;
const FloatShell = styled.div<{ $focused?: boolean; $error?: boolean }>`
  position: relative;
  height: 54px;
  border: 1px solid
    ${({ $error, $focused }) =>
      $error
        ? T.color.textError
        : $focused
          ? T.color.borderBrand
          : T.color.borderDefault};
  border-radius: ${T.radius.sm};
  background: ${T.color.bgPrimary};
  box-sizing: border-box;
`;

const FloatLabel = styled.span<{ $focused?: boolean; $raised?: boolean }>`
  position: absolute;
  left: 11px;
  top: -9px;
  z-index: 1;
  padding: 0 4px;
  background: ${T.color.bgPrimary};
  font-family: ${T.font.sans};
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 16px;
  color: ${({ $focused }) =>
    $focused ? T.color.borderBrand : T.color.textTertiary};
  pointer-events: none;
  opacity: ${({ $raised }) => ($raised ? 1 : 0)};
  transition: opacity 0.12s ease;
`;

const FloatInput = styled.input`
  appearance: none;
  width: 100%;
  height: 54px;
  padding: ${T.space[16]};
  box-sizing: border-box;
  border: none;
  border-radius: ${T.radius.sm};
  background: transparent;
  font-family: ${T.font.sans};
  font-weight: ${FontWeight.Regular};
  font-size: 16px;
  line-height: 24px;
  color: ${T.color.textPrimary};
  outline: none;

  ${MOBILE} {
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.3px;

    /* iOS Safari zooms inputs < 16px; Android can keep Figma 14px. */
    @supports (-webkit-touch-callout: none) {
      font-size: 16px;
      line-height: 24px;
      letter-spacing: normal;
    }
  }

  &::placeholder {
    color: ${T.color.textTertiary};
  }
`;

const PhoneInputRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 54px;
  padding-right: ${T.space[16]};
  box-sizing: border-box;
`;

const PhoneFloatInput = styled(FloatInput)`
  flex: 1;
  min-width: 0;
  padding-right: ${T.space[8]};
`;

/** Clear icon box 20×20; vector leaf ~12×12. */
const ClearButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;

  img {
    display: block;
    width: 12px;
    height: 12px;
  }
`;

const FieldError = styled.p`
  margin: 2px 0 0;
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 16px;
  color: ${T.color.textError};
`;

const ErrorBanner = styled.div`
  width: 100%;
  padding: ${T.space[12]};
  box-sizing: border-box;
  border-radius: ${T.radius.xs};
  background: rgba(231, 76, 60, 0.12);
  font-size: 14px;
  font-weight: ${FontWeight.Medium};
  line-height: 20px;
  color: ${T.color.textPrimary};
`;

const PrimaryButton = styled.button<{ disabled?: boolean }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 48px;
  padding: 0 ${T.space[24]};
  border: none;
  border-radius: ${T.radius.md};
  background: ${({ disabled }) =>
    disabled ? T.color.bgButtonDisabled : T.color.textBrand};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  font-family: ${T.font.sans};
  font-weight: ${FontWeight.Semibold};
  color: ${T.color.textInverse};
  white-space: nowrap;

  ${DESKTOP} {
    font-size: 16px;
    line-height: 16px;
  }

  ${MOBILE} {
    font-size: 14px;
    line-height: 12px;
  }

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 1 : 0.9)};
  }
`;

const SuccessHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${T.space[8]};
  width: 100%;
`;

/**
 * Figma icon-with-bg: outer pad 4, brand tint square 28×28, icon box 20×20.
 */
const SuccessIconWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 4px;
  box-sizing: border-box;
  border-radius: ${T.radius.sm};
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: ${T.radius.sm};
    background: rgba(8, 170, 168, 0.25);
  }
`;

const SuccessIconBox = styled.div<{ $kind: 'check' | 'headset' }>`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;

  img {
    display: block;
    width: ${({ $kind }) => ($kind === 'check' ? '15px' : '17px')};
    height: ${({ $kind }) => ($kind === 'check' ? '11px' : '15px')};
  }
`;

const SuccessTitle = styled.h2`
  margin: 0;
  flex: 1;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};

  ${DESKTOP} {
    font-size: 20px;
    line-height: 23px;
  }

  ${MOBILE} {
    font-size: 16px;
    line-height: 22px;
  }
`;

const SuccessCopy = styled.p`
  margin: 0;
  width: 100%;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};

  ${DESKTOP} {
    font-size: 16px;
    line-height: 24px;
  }

  ${MOBILE} {
    font-size: 14px;
    line-height: 20px;
  }
`;

const IMMEDIATE_DURATION = Duration.fromObject({ minutes: 10 }).toISO()!;

const formatSlotLabel = (window: CallbackWindow) =>
  formatCallbackWindow(window)
    .replace(/AM/g, ' AM')
    .replace(/PM/g, ' PM')
    .replace(/\s+/g, ' ')
    .trim();

const formatScheduledSuccess = (window: CallbackWindow) => {
  const from = DateTime.fromISO(window.time);
  const till = from.plus(Duration.fromISO(window.duration));
  const date = from.toFormat('ccc, LLL d');
  const start = from.toFormat('h:mm a');
  const end = till.toFormat('h:mm a');
  return `A Clutter agent will call you back on ${date} between ${start} and ${end}.`;
};

type Step = 'form' | 'success';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Nav Talk / phone control — desktop popover anchors under this element. */
  anchorRef?: React.RefObject<HTMLElement>;
  /** Sticky challenger nav — mobile sheet clears this bar (Figma 707:23349). */
  navRef?: React.RefObject<HTMLElement>;
};

type FloatFieldProps = {
  id: string;
  label: string;
  value: string;
  type?: string;
  autoComplete?: string;
  error?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  showClear?: boolean;
  onClear?: () => void;
  onChange: (value: string) => void;
  onFocusChange?: (focused: boolean) => void;
};

const ExpertFloatField: React.FC<FloatFieldProps> = ({
  id,
  label,
  value,
  type = 'text',
  autoComplete,
  error,
  inputRef,
  showClear,
  onClear,
  onChange,
  onFocusChange,
}) => {
  const [focused, setFocused] = useState(false);
  const raised = focused || value.trim().length > 0;

  return (
    <FloatField id={id}>
      <FloatShell $focused={focused} $error={error}>
        <FloatLabel $focused={focused} $raised={raised}>
          {label}
        </FloatLabel>
        {showClear ? (
          <PhoneInputRow>
            <PhoneFloatInput
              ref={inputRef}
              type={type}
              value={value}
              autoComplete={autoComplete}
              placeholder={raised ? '' : label}
              aria-label={label}
              aria-invalid={error || undefined}
              onFocus={() => {
                setFocused(true);
                onFocusChange?.(true);
              }}
              onBlur={() => {
                setFocused(false);
                onFocusChange?.(false);
              }}
              onChange={(e) => onChange(e.target.value)}
            />
            {value.trim().length > 0 && focused && (
              <ClearButton
                type="button"
                aria-label={`Clear ${label}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={onClear}
              >
                <img src={closeIcon} alt="" />
              </ClearButton>
            )}
          </PhoneInputRow>
        ) : (
          <FloatInput
            ref={inputRef}
            type={type}
            value={value}
            autoComplete={autoComplete}
            placeholder={raised ? '' : label}
            aria-label={label}
            aria-invalid={error || undefined}
            onFocus={() => {
              setFocused(true);
              onFocusChange?.(true);
            }}
            onBlur={() => {
              setFocused(false);
              onFocusChange?.(false);
            }}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
      </FloatShell>
    </FloatField>
  );
};

export const TalkWithExpertModal: React.FC<Props> = ({
  isOpen,
  onClose,
  anchorRef,
  navRef,
}) => {
  const track = useTrack();
  const createLead = useCreateLead();
  const [createCallback] = useMutation(ZendeskCallbackRequestCreateDocument);
  const {
    flowState: {
      values: { email, zip, name: checkoutName, phone: checkoutPhone },
      onChange,
    },
  } = useStorageCheckoutContext();

  const [now, setNow] = useState(() => DateTime.local().toISO());
  const { data, loading: windowsLoading } = useQuery(CallbackWindowsDocument, {
    fetchPolicy: 'network-only',
    skip: !isOpen,
  });

  const [step, setStep] = useState<Step>('form');
  const [selectedWindow, setSelectedWindow] = useState<CallbackWindow>();
  const [scheduledWindow, setScheduledWindow] = useState<CallbackWindow>();
  const [confirmedPhone, setConfirmedPhone] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [nameError, setNameError] = useState<string>();
  const [phoneError, setPhoneError] = useState<string>();
  const [formError, setFormError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [popoverPos, setPopoverPos] = useState<{
    top: number;
    right: number;
  }>();
  const [mobileOffsetTop, setMobileOffsetTop] = useState(72);
  const phoneRef = useRef<HTMLInputElement>(null);
  const maskRef = useRef<ReturnType<typeof createTextMaskInputElement>>();
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    setNow(DateTime.local().toISO());
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;
    setStep('form');
    setSelectedWindow(undefined);
    setScheduledWindow(undefined);
    setConfirmedPhone('');
    setName(checkoutName || '');
    setPhone(checkoutPhone || '');
    setNameError(undefined);
    setPhoneError(undefined);
    setFormError(undefined);
    setSubmitting(false);
  }, [isOpen, checkoutName, checkoutPhone]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const isMobile = window.innerWidth < 840;
      const navEl = navRef?.current;
      if (isMobile) {
        setPopoverPos(undefined);
        // Clear sticky nav + 16px gap so the card title isn’t clipped (707:23363).
        const navBottom = navEl?.getBoundingClientRect().bottom ?? 56;
        setMobileOffsetTop(Math.round(navBottom));
        return;
      }

      const el = anchorRef?.current;
      if (!el) {
        setPopoverPos(undefined);
        return;
      }
      const rect = el.getBoundingClientRect();
      const right = Math.max(16, window.innerWidth - rect.right);
      // Keep popover within viewport when Talk CTA is far left of right edge.
      const minRight = 16;
      const maxRight = Math.max(
        minRight,
        window.innerWidth - POPOVER_WIDTH - 16,
      );
      setPopoverPos({
        top: Math.round(rect.bottom + POPOVER_GAP),
        right: Math.min(Math.max(right, minRight), maxRight),
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, anchorRef, navRef]);

  useEffect(() => {
    if (!isOpen || step !== 'form' || !phoneRef.current) return;
    maskRef.current = createTextMaskInputElement({
      inputElement: phoneRef.current,
      mask: PHONE_MASK,
      guide: false,
    });
    maskRef.current.update(phone);
    // Only (re)bind mask when the form mounts / reopens — not on every phone keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step]);

  const selectWindow = (window: CallbackWindow) => {
    setFormError(undefined);
    setSelectedWindow(window);
    track({
      action: 'click',
      objectType: 'button',
      objectName: 'callback_window',
      value: window.time,
      metadata: {
        duration: window.duration,
        callback_type: window.time === now ? 'immediate' : 'scheduled',
      },
    });
  };

  const handleConfirm = async () => {
    if (submitting) return;

    setNameError(undefined);
    setPhoneError(undefined);
    setFormError(undefined);

    const trimmedName = name.trim();
    let invalid = false;
    if (!trimmedName || trimmedName.split(/\s+/).length < 2) {
      setNameError('Please provide your full name.');
      invalid = true;
    }
    if (!phone || !phoneValidator(phone)) {
      setPhoneError('Please enter a valid phone number.');
      invalid = true;
    }
    if (!selectedWindow) {
      setFormError('Please select a time for us to call you.');
      invalid = true;
    }
    if (invalid) return;

    track({
      action: 'click',
      objectName: 'callback_submit',
      objectType: 'button',
      label: 'Confirm call',
      metadata: {
        callback_time: selectedWindow!.time,
        duration: selectedWindow!.duration,
      },
    });

    setSubmitting(true);
    try {
      const { id: leadID } = await createLead({
        leadAttributes: {
          name: titleize(trimmedName),
          phone,
          email,
          zip,
        },
        serviceContext: ServiceEnum.DoorToDoor,
      });

      if (!leadID) {
        setFormError('Unable to schedule your call. Please try again.');
        return;
      }

      onChange('name', titleize(trimmedName));
      onChange('phone', phone);

      const callbackAt =
        selectedWindow!.duration === IMMEDIATE_DURATION
          ? DateTime.local().toISO()
          : selectedWindow!.time;

      const { data: resultData } = await createCallback({
        variables: {
          leadID,
          callbackAt,
          contactPreference: CustomerTicket__ContactPreference.Phone,
          visitorToken: WT_VISITOR_TOKEN,
        },
      });

      if (resultData?.result?.error) {
        throw new GQLError(resultData.result.error);
      }

      setConfirmedPhone(phone);
      setScheduledWindow(selectedWindow!);
      setStep('success');
      track({
        action: 'display',
        objectName: 'callback_create_success_message',
        metadata: {
          callback_time: selectedWindow?.time,
          duration: selectedWindow?.duration,
          lead_id: leadID,
        },
      });
    } catch (error) {
      if (error instanceof GQLError) {
        if (error.fullError.errorCode === ErrorCodeEnum.InvalidPhone) {
          setPhoneError('Please enter a valid phone number.');
        } else {
          setFormError(
            error.fullError.errorMessage ||
              'Unable to schedule your call. Please try again.',
          );
        }
      } else {
        setFormError('Unable to schedule your call. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || typeof document === 'undefined') return null;

  const onOverlayClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (event.currentTarget === event.target) onClose();
  };

  const isImmediate =
    !!scheduledWindow &&
    (scheduledWindow.time === now ||
      scheduledWindow.duration === IMMEDIATE_DURATION);

  if (step === 'success' && scheduledWindow) {
    return createPortal(
      <Overlay
        $offsetTop={mobileOffsetTop}
        onClick={onOverlayClick}
        role="presentation"
      >
        <SuccessDialog
          role="dialog"
          aria-modal="true"
          aria-labelledby="challenger-talk-expert-success-title"
          $top={popoverPos?.top}
          $right={popoverPos?.right}
          $offsetTop={mobileOffsetTop}
          onClick={(event) => event.stopPropagation()}
        >
          <SuccessHeader>
            <SuccessIconWrap aria-hidden>
              <SuccessIconBox $kind={isImmediate ? 'headset' : 'check'}>
                <img
                  src={isImmediate ? callingYouHeadset : callConfirmedCheck}
                  alt=""
                />
              </SuccessIconBox>
            </SuccessIconWrap>
            <SuccessTitle id="challenger-talk-expert-success-title">
              {isImmediate ? 'We’re calling you...' : 'Call confirmed!'}
            </SuccessTitle>
            <CloseButton type="button" aria-label="Close" onClick={onClose}>
              <img src={closeIcon} alt="" />
            </CloseButton>
          </SuccessHeader>
          <SuccessCopy>
            {isImmediate
              ? `A Clutter agent will call you at ${confirmedPhone} shortly.`
              : formatScheduledSuccess(scheduledWindow)}
          </SuccessCopy>
        </SuccessDialog>
      </Overlay>,
      document.body,
    );
  }

  const allowImmediate = data?.allowImmediateCallback;
  const callbackWindows = data?.callbackWindows ?? [];
  const businessDay = callbackWindows[0]
    ? DateTime.fromISO(callbackWindows[0].time)
    : undefined;
  const sameDayCallbacks =
    !!businessDay &&
    businessDay.startOf('day').toMillis() ===
      DateTime.fromISO(now).startOf('day').toMillis();
  const immediateSelected = selectedWindow?.time === now;

  return createPortal(
    <Overlay
      $offsetTop={mobileOffsetTop}
      onClick={onOverlayClick}
      role="presentation"
    >
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="challenger-talk-expert-title"
        $top={popoverPos?.top}
        $right={popoverPos?.right}
        $offsetTop={mobileOffsetTop}
        onClick={(event) => event.stopPropagation()}
      >
        <TopBlock>
          <HeaderBlock>
            <HeaderRow>
              <Title id="challenger-talk-expert-title">
                Find the best fit for your items
              </Title>
              <CloseButton type="button" aria-label="Close" onClick={onClose}>
                <img src={closeIcon} alt="" />
              </CloseButton>
            </HeaderRow>
            <Subtitle>
              Get a callback today, at a time that works for you.
            </Subtitle>
          </HeaderBlock>

          {windowsLoading || !data ? (
            <LoaderWrap>
              <SpinLoader />
            </LoaderWrap>
          ) : (
            <>
              {allowImmediate && (
                <CallNowCard
                  type="button"
                  $selected={immediateSelected}
                  onClick={() =>
                    selectWindow({
                      time: now,
                      duration: IMMEDIATE_DURATION,
                    })
                  }
                >
                  <CallNowBody>
                    <AvailableRow>
                      <AvailableDot aria-hidden />
                      <AvailableLabel>Available now</AvailableLabel>
                    </AvailableRow>
                    <CallNowLabel>Call me now</CallNowLabel>
                  </CallNowBody>
                  <PhoneIconWrap aria-hidden>
                    <img src={phoneIcon} alt="" />
                  </PhoneIconWrap>
                </CallNowCard>
              )}

              <TimesSection>
                <OrPickLabel>Or pick a time</OrPickLabel>
                <SlotGrid>
                  {!sameDayCallbacks && businessDay && (
                    <DayNote>
                      Schedule a call for{' '}
                      {businessDay.toLocaleString(DateTime.DATE_MED)}
                    </DayNote>
                  )}
                  {callbackWindows.map((window) => {
                    const selected =
                      !!selectedWindow &&
                      !immediateSelected &&
                      DateTime.fromISO(selectedWindow.time).toMillis() ===
                        DateTime.fromISO(window.time).toMillis();
                    return (
                      <SlotButton
                        key={window.time}
                        type="button"
                        $selected={selected}
                        onClick={() => selectWindow(window)}
                      >
                        {formatSlotLabel(window)}
                      </SlotButton>
                    );
                  })}
                </SlotGrid>
              </TimesSection>
            </>
          )}
        </TopBlock>

        <BottomBlock>
          <SectionTitle>Your information</SectionTitle>
          <Fields>
            <FieldSlot>
              <ExpertFloatField
                id="talk-expert-name"
                label="Full name"
                value={name}
                autoComplete="name"
                error={!!nameError}
                onChange={(value) => {
                  setNameError(undefined);
                  setName(value);
                }}
              />
              {nameError && <FieldError>{nameError}</FieldError>}
            </FieldSlot>
            <FieldSlot>
              <ExpertFloatField
                id="talk-expert-phone"
                label="Mobile number"
                type="tel"
                autoComplete="tel"
                value={phone}
                error={!!phoneError}
                inputRef={phoneRef}
                showClear
                onClear={() => {
                  setPhoneError(undefined);
                  setPhone('');
                  maskRef.current?.update('');
                }}
                onChange={(value) => {
                  setPhoneError(undefined);
                  maskRef.current?.update();
                  setPhone(value);
                }}
              />
              {phoneError && <FieldError>{phoneError}</FieldError>}
            </FieldSlot>
          </Fields>
          {formError && <ErrorBanner>{formError}</ErrorBanner>}
          <PrimaryButton
            type="button"
            disabled={submitting}
            onClick={handleConfirm}
          >
            Confirm call
          </PrimaryButton>
        </BottomBlock>
      </Dialog>
    </Overlay>,
    document.body,
  );
};
